import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import * as authService from "../services/autService";
import * as profileService from "../services/profileService";
import { registerAuthHandlers, setAuthToken } from "../services/httpClient";
import { useToast } from "../components/ui/use-toast";
import type {
  AuthSession,
  ChangePasswordPayload,
  UpdateTradingIndicatorsPayload,
  UpdatePreferencesPayload,
  UpdateProfilePayload,
  User,
} from "../types/api";

interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: { email: string; password: string }) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (payload: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<string | null>;
  reloadProfile: () => Promise<void>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<void>;
  updatePreferences: (payload: UpdatePreferencesPayload) => Promise<void>;
  updateTradingIndicators: (
    payload: UpdateTradingIndicatorsPayload
  ) => Promise<void>;
  changePassword: (payload: ChangePasswordPayload) => Promise<void>;
}

const LOCAL_STORAGE_KEY = "cryptinvest.auth";
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

const readPersistedState = (): AuthSession | null => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AuthSession | null>(() =>
    readPersistedState()
  );
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const isLoggingOutRef = useRef(false);
  const hasUnauthorizedRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasIdleWarningRef = useRef(false);

  const redirectToLogin = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const path = window.location.pathname;
    if (path !== "/login") {
      window.location.replace("/login");
    }
  }, []);

  const showIdleWarning = useCallback(() => {
    if (hasIdleWarningRef.current) {
      return;
    }
    hasIdleWarningRef.current = true;
    toast({
      title: t("auth.sessionExpiredTitle"),
      description: t("auth.sessionExpiredIdleDescription"),
      variant: "info",
    });
  }, [t, toast]);

  const persistSession = useCallback((payload: AuthSession | null) => {
    if (payload) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      setAuthToken(payload.tokens.accessToken);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setAuthToken(null);
    }
    setSession(payload);
  }, []);

  const logout = useCallback(async () => {
    isLoggingOutRef.current = true;
    // Déconnexion locale immédiate, même si l'API répond 401
    persistSession(null);
    if (session?.tokens.refreshToken && !hasUnauthorizedRef.current) {
      try {
        await authService.logout({
          refreshToken: session.tokens.refreshToken,
        });
      } catch {
        // Ignorer les erreurs réseau lors du logout
      }
    }
  }, [persistSession, session]);

  const getIdleFor = useCallback(
    () => Date.now() - lastActivityRef.current,
    []
  );

  const scheduleIdleLogout = useCallback(() => {
    if (!session) {
      return;
    }
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    const remaining = IDLE_TIMEOUT_MS - getIdleFor();
    if (remaining <= 0) {
      void logout();
      redirectToLogin();
      return;
    }
    idleTimeoutRef.current = setTimeout(() => {
      const idleFor = getIdleFor();
      if (idleFor >= IDLE_TIMEOUT_MS) {
        void logout();
        redirectToLogin();
      } else {
        scheduleIdleLogout();
      }
    }, remaining);
  }, [getIdleFor, logout, redirectToLogin, session]);

  const refreshSession = useCallback(async () => {
    if (isLoggingOutRef.current) {
      return null;
    }
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }
    if (!session?.tokens.refreshToken) {
      persistSession(null);
      return null;
    }
    const refreshTask = (async () => {
      try {
        const data = await authService.refresh({
          refreshToken: session.tokens.refreshToken,
        });
        if (isLoggingOutRef.current) {
          return null;
        }
        persistSession(data);
        return data.tokens.accessToken;
      } catch (error) {
        const idleFor = getIdleFor();
        if (idleFor < IDLE_TIMEOUT_MS) {
          hasUnauthorizedRef.current = true;
          showIdleWarning();
          scheduleIdleLogout();
          return null;
        }
        persistSession(null);
        throw error;
      }
    })();
    refreshPromiseRef.current = refreshTask;
    try {
      return await refreshTask;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [getIdleFor, persistSession, scheduleIdleLogout, session]);

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (session) {
      scheduleIdleLogout();
    }
  }, [session, scheduleIdleLogout]);

  useEffect(() => {
    registerAuthHandlers({
      getAccessToken: () => session?.tokens.accessToken ?? null,
      refreshAccessToken: refreshSession,
      onActivity: recordActivity,
      onUnauthorized: () => {
        if (hasUnauthorizedRef.current) {
          return;
        }
        const idleFor = Date.now() - lastActivityRef.current;
        if (idleFor < IDLE_TIMEOUT_MS) {
          hasUnauthorizedRef.current = true;
          showIdleWarning();
          scheduleIdleLogout();
          return;
        }
        hasUnauthorizedRef.current = true;
        persistSession(null);
        redirectToLogin();
      },
    });
  }, [
    persistSession,
    recordActivity,
    redirectToLogin,
    refreshSession,
    scheduleIdleLogout,
    session?.tokens.accessToken,
  ]);

  useEffect(() => {
    setAuthToken(session?.tokens.accessToken ?? null);
  }, [session?.tokens.accessToken]);
  useEffect(() => {
    if (!session) {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      hasIdleWarningRef.current = false;
      return;
    }
    lastActivityRef.current = Date.now();
    hasIdleWarningRef.current = false;
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      scheduleIdleLogout();
    };
    const events = [
      "mousemove",
      "mousedown",
      "mouseup",
      "keydown",
      "keyup",
      "scroll",
      "touchstart",
      "click",
      "pointerdown",
    ];
    events.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastActivityRef.current = Date.now();
        scheduleIdleLogout();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    scheduleIdleLogout();
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };
  }, [scheduleIdleLogout, session]);

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      isLoggingOutRef.current = false;
      setIsLoading(true);
      try {
        const data = await authService.login(payload);
        hasUnauthorizedRef.current = false;
        persistSession(data);
      } finally {
        setIsLoading(false);
      }
    },
    [persistSession]
  );

  const register = useCallback(
    async (payload: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      password: string;
    }) => {
      isLoggingOutRef.current = false;
      setIsLoading(true);
      try {
        hasUnauthorizedRef.current = false;
        await authService.register(payload);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const loginWithGoogle = useCallback(
    async (credential: string) => {
      isLoggingOutRef.current = false;
      setIsLoading(true);
      try {
        const data = await authService.loginWithGoogle({ credential });
        hasUnauthorizedRef.current = false;
        persistSession(data);
      } finally {
        setIsLoading(false);
      }
    },
    [persistSession]
  );

  const ensureAuthenticated = useCallback(() => {
    if (!session) {
      throw new Error("Utilisateur non authentifié");
    }
    return session;
  }, [session]);

  const updateSessionUser = useCallback(
    (user: User) => {
      const current = ensureAuthenticated();
      persistSession({ ...current, user });
    },
    [ensureAuthenticated, persistSession]
  );

  const reloadProfile = useCallback(async () => {
    const current = ensureAuthenticated();
    const user = await authService.me();
    persistSession({ ...current, user });
  }, [ensureAuthenticated, persistSession]);

  const updateProfile = useCallback(
    async (payload: UpdateProfilePayload) => {
      const user = await profileService.updateProfile(payload);
      updateSessionUser(user);
    },
    [updateSessionUser]
  );

  const updatePreferences = useCallback(
    async (payload: UpdatePreferencesPayload) => {
      const user = await profileService.updatePreferences(payload);
      updateSessionUser(user);
    },
    [updateSessionUser]
  );

  const updateTradingIndicators = useCallback(
    async (payload: UpdateTradingIndicatorsPayload) => {
      const user = await profileService.updateTradingIndicators(payload);
      updateSessionUser(user);
    },
    [updateSessionUser]
  );

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    await profileService.changePassword(payload);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      accessToken: session?.tokens.accessToken ?? null,
      refreshToken: session?.tokens.refreshToken ?? null,
      isAuthenticated: Boolean(session),
      isLoading,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshSession,
      reloadProfile,
      updateProfile,
      updatePreferences,
      updateTradingIndicators,
      changePassword,
    }),
    [
      changePassword,
      isLoading,
      login,
      loginWithGoogle,
      logout,
      refreshSession,
      register,
      reloadProfile,
      session,
      updatePreferences,
      updateProfile,
      updateTradingIndicators,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
